import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, AlertTriangle, ScrollText, LogOut, CheckCircle, XCircle, Shield, Eye, Clock, Building2, DollarSign, FileCheck, Scale, RotateCcw, Send, ChevronRight, X, Briefcase, Mail, Calendar, MapPin, Ban, UserCheck, Search, Info, ExternalLink, LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-50 text-emerald-600 border-emerald-200', verified: 'bg-emerald-50 text-emerald-600 border-emerald-200', completed: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    pending: 'bg-amber-50 text-amber-600 border-amber-200', open: 'bg-amber-50 text-amber-600 border-amber-200', unverified: 'bg-slate-100 text-slate-500 border-slate-200',
    resolved: 'bg-emerald-50 text-emerald-600 border-emerald-200', dismissed: 'bg-slate-100 text-slate-500 border-slate-200',
    accepted: 'bg-emerald-50 text-emerald-600 border-emerald-200', sent: 'bg-blue-50 text-blue-600 border-blue-200',
    countered: 'bg-amber-50 text-amber-600 border-amber-200', refused: 'bg-red-50 text-red-600 border-red-200',
    suspended: 'bg-red-50 text-red-600 border-red-200',
  };
  return <Badge variant="outline" className={`text-xs font-medium ${map[status] || 'bg-slate-50 text-slate-500 border-slate-200'}`}>{status}</Badge>;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [mediationCases, setMediationCases] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [s, u, d, l, a, m] = await Promise.all([
        API.get('/admin/stats'), API.get('/admin/users'), API.get('/admin/disputes'),
        API.get('/admin/audit-logs?limit=200'), API.get('/admin/agencies'),
        API.get('/admin/mediation/cases'),
      ]);
      setStats(s.data); setUsers(u.data.users); setDisputes(d.data.disputes);
      setLogs(l.data.logs); setAgencies(a.data.agencies);
      setMediationCases(m.data.cases);
    } catch { toast.error('Failed to load admin data'); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users, count: users.length },
    { id: 'agencies', label: 'Agencies', icon: Building2, count: agencies.length },
    { id: 'mediation', label: 'Mediation', icon: Scale, count: mediationCases.filter(c => c.status === 'open').length },
    { id: 'disputes', label: 'Disputes', icon: AlertTriangle, count: disputes.filter(d => d.status === 'open').length },
    { id: 'audit', label: 'Audit Logs', icon: ScrollText },
    { id: 'support', label: 'Support Tickets', icon: LifeBuoy },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-100">
          <button onClick={() => navigate('/')} className="font-heading text-xl font-bold text-slate-900">Mylikeness</button>
          <p className="text-red-600 text-xs font-medium mt-0.5">Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <button key={item.id} data-testid={`nav-${item.id}`} onClick={() => setTab(item.id)}
              className={`sidebar-item w-full ${tab === item.id ? 'sidebar-item-active' : 'sidebar-item-inactive'}`}>
              <item.icon className="w-4 h-4" />{item.label}
              {item.count > 0 && <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{item.count}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="w-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 text-xs">
            <LogOut className="w-3 h-3 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        {tab === 'overview' && <AdminOverview stats={stats} />}
        {tab === 'users' && <UsersPanel users={users} onRefresh={fetchData} />}
        {tab === 'agencies' && <AgenciesPanel agencies={agencies} onRefresh={fetchData} />}
        {tab === 'mediation' && <MediationAdminPanel cases={mediationCases} onRefresh={fetchData} />}
        {tab === 'disputes' && <DisputesPanel disputes={disputes} onRefresh={fetchData} />}
        {tab === 'audit' && <AuditLogsPanel logs={logs} users={users} onRefresh={fetchData} />}
        {tab === 'support' && <SupportTicketsPanel />}
      </main>
    </div>
  );
}

/* ═══════════════════ Overview ═══════════════════ */
function AdminOverview({ stats }) {
  const items = [
    { label: 'Total Users', value: stats.total_users || 0, icon: Users, color: 'bg-indigo-50 text-indigo-600' },
    { label: 'Creators', value: stats.total_creators || 0, icon: Shield, color: 'bg-purple-50 text-purple-600' },
    { label: 'Agencies', value: stats.total_agencies || 0, icon: Building2, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Campaigns', value: stats.total_campaigns || 0, icon: Briefcase, color: 'bg-blue-50 text-blue-600' },
    { label: 'Total Deals', value: stats.total_deals || 0, icon: FileCheck, color: 'bg-cyan-50 text-cyan-600' },
    { label: 'Active Deals', value: stats.active_deals || 0, icon: CheckCircle, color: 'bg-teal-50 text-teal-600' },
    { label: 'Open Disputes', value: stats.open_disputes || 0, icon: AlertTriangle, color: 'bg-amber-50 text-amber-600' },
    { label: 'Total Revenue', value: `$${(stats.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'bg-green-50 text-green-600' },
  ];
  return (
    <div data-testid="admin-overview">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Platform Overview</h1>
        <p className="text-slate-500 text-sm mt-1">Monitor platform activity and manage operations</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map(s => (
          <div key={s.label} className="stat-card">
            <div className={`w-10 h-10 rounded-xl ${s.color} flex items-center justify-center mb-4`}>
              <s.icon className="w-5 h-5" />
            </div>
            <p className="font-heading text-3xl font-bold text-slate-900">{s.value}</p>
            <p className="text-slate-500 text-sm mt-1">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════ Users Panel + Drill-in ═══════════════════ */
function UsersPanel({ users, onRefresh }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [needsAddrOnly, setNeedsAddrOnly] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const needsAddrCount = users.filter(u => u.needs_address_verification).length;

  const filtered = users.filter(u => {
    if (filter !== 'all' && u.role !== filter) return false;
    if (needsAddrOnly && !u.needs_address_verification) return false;
    if (search) {
      const q = search.toLowerCase();
      return (u.display_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const openDetail = async (u) => {
    setSelectedUser(u);
    setDetailLoading(true);
    try {
      const res = await API.get(`/admin/users/${u.id}`);
      setUserDetail(res.data.user);
    } catch { toast.error('Failed to load user details'); }
    finally { setDetailLoading(false); }
  };

  const toggleSuspend = async (userId) => {
    try {
      const res = await API.patch(`/admin/users/${userId}/suspend`);
      toast.success(res.data.suspended ? 'User suspended' : 'User unsuspended');
      onRefresh();
      if (userDetail?.id === userId) {
        setUserDetail(prev => ({ ...prev, suspended: res.data.suspended }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleVerification = async (userId, current) => {
    const newStatus = current === 'verified' ? 'unverified' : 'verified';
    try {
      await API.patch(`/admin/users/${userId}/verification`, { verification_status: newStatus, verification_notes: `${newStatus} by admin` });
      toast.success(`User ${newStatus}`); onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="admin-users-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Users</h2>
          <p className="text-slate-500 text-sm mt-1">Manage platform users — click any row to view details</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setNeedsAddrOnly(v => !v)}
            data-testid="users-filter-needs-address"
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${needsAddrOnly ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'}`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Needs Address Verify
            <span className={`ml-1 px-1.5 rounded-full text-[10px] ${needsAddrOnly ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-500'}`}>{needsAddrCount}</span>
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="users-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name or email..." className="pl-9 w-56 bg-white border-slate-200" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="users-filter" className="w-44 bg-white border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All Roles</SelectItem><SelectItem value="creator">Creators</SelectItem>
              <SelectItem value="buyer">Buyers</SelectItem><SelectItem value="agency_admin">Agency Admins</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_140px_120px_100px] gap-4 px-5 py-3 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase tracking-wider">
          <span className="w-10" />
          <span>User</span>
          <span>Role</span>
          <span>Status</span>
          <span>Joined</span>
        </div>
        <div className="divide-y divide-slate-100">
          {filtered.map(u => (
            <button key={u.id} data-testid={`user-row-${u.id}`} onClick={() => openDetail(u)}
              className="w-full grid grid-cols-[auto_1fr_140px_120px_100px] gap-4 px-5 py-3.5 items-center hover:bg-slate-50 transition-colors text-left cursor-pointer">
              <Avatar className="w-9 h-9 border border-slate-200">
                <AvatarImage src={u.creator_profile?.avatar_url} />
                <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs font-semibold">{(u.display_name || '?')[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-slate-900 font-medium text-sm truncate">{u.display_name}</p>
                <p className="text-slate-500 text-xs truncate">{u.email}</p>
              </div>
              <Badge variant="outline" className="text-xs text-slate-500 border-slate-200 bg-slate-50 w-fit">{u.role}</Badge>
              <div>
                {u.suspended ? <StatusBadge status="suspended" /> :
                 u.role === 'creator' && u.creator_profile ? <StatusBadge status={u.creator_profile.verification_status || 'unverified'} /> :
                 <StatusBadge status="active" />}
              </div>
              <span className="text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</span>
            </button>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No users match your filters.</p>
          </div>
        )}
      </div>

      {/* User Drill-in Sheet */}
      <Sheet open={!!selectedUser} onOpenChange={o => { if (!o) { setSelectedUser(null); setUserDetail(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="user-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-lg font-heading">User Detail</SheetTitle>
            <SheetDescription className="sr-only">Detailed view of the selected user</SheetDescription>
          </SheetHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-20"><div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full" /></div>
          ) : userDetail ? (
            <div className="mt-4 space-y-5">
              {/* Header */}
              <div className="flex items-center gap-4">
                <Avatar className="w-14 h-14 border-2 border-slate-200">
                  <AvatarImage src={userDetail.creator_profile?.avatar_url} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 font-bold text-lg">{(userDetail.display_name || '?')[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="text-slate-900 font-semibold text-base">{userDetail.display_name}</h3>
                  <p className="text-slate-500 text-sm">{userDetail.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{userDetail.role}</Badge>
                    {userDetail.suspended && <StatusBadge status="suspended" />}
                    {userDetail.email_verified && <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">Email verified</Badge>}
                  </div>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                <InfoRow icon={Calendar} label="Joined" value={userDetail.created_at ? new Date(userDetail.created_at).toLocaleDateString() : '-'} />
                <InfoRow icon={Mail} label="Email" value={userDetail.email} />
                {userDetail.creator_profile?.date_of_birth && (
                  <InfoRow icon={Calendar} label="DOB" value={userDetail.creator_profile.date_of_birth} />
                )}
                {userDetail.creator_profile?.address?.formatted_address && (
                  <InfoRow icon={MapPin} label="Location" value={userDetail.creator_profile.address.formatted_address} />
                )}
                {userDetail.agency && (
                  <InfoRow icon={Building2} label="Agency" value={userDetail.agency.name} />
                )}
              </div>

              {/* Creator-specific: verification */}
              {userDetail.role === 'creator' && userDetail.creator_profile && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">Verification</h4>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Identity status</span>
                    <StatusBadge status={userDetail.creator_profile.verification_status || 'unverified'} />
                  </div>
                  {userDetail.creator_profile.stripe_account_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Stripe Connect</span>
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">Connected</Badge>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Profile completed</span>
                    {userDetail.profile_completed ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-slate-300" />}
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-sm text-slate-600">Toggle verification</span>
                    <Switch data-testid="detail-verify-toggle" checked={userDetail.creator_profile.verification_status === 'verified'}
                      onCheckedChange={() => toggleVerification(userDetail.id, userDetail.creator_profile.verification_status)} />
                  </div>
                </div>
              )}

              {/* Creator niches/bio */}
              {userDetail.creator_profile?.niches?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Niches</h4>
                  <div className="flex flex-wrap gap-1.5">{userDetail.creator_profile.niches.map(n => <Badge key={n} variant="outline" className="text-xs">{n}</Badge>)}</div>
                </div>
              )}
              {userDetail.creator_profile?.bio && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">Bio</h4>
                  <p className="text-sm text-slate-600">{userDetail.creator_profile.bio}</p>
                </div>
              )}

              {/* Deal history */}
              {userDetail.deals && userDetail.deals.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Deal History ({userDetail.deals.length})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {userDetail.deals.map(d => (
                      <div key={d.id} className="bg-slate-50 rounded-lg p-3 text-xs space-y-1" data-testid={`user-deal-${d.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-800">{d.campaign_name}</span>
                          <StatusBadge status={d.proposal_status || d.status || 'unknown'} />
                        </div>
                        <div className="flex items-center justify-between text-slate-500">
                          <span>{d.agency_name}</span>
                          <span>{d.proposal?.commercial_offer?.base_amount ? `$${d.proposal.commercial_offer.base_amount.toLocaleString()}` : '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transactions */}
              {userDetail.transactions && userDetail.transactions.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Recent Transactions</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {userDetail.transactions.map(t => (
                      <div key={t.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3 text-xs">
                        <div>
                          <span className="font-medium text-slate-700 capitalize">{(t.type || '').replace(/_/g, ' ')}</span>
                          <span className="text-slate-400 ml-2">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                        </div>
                        <span className="font-semibold text-slate-900">{t.currency || '$'} {(t.amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-3 border-t border-slate-200 space-y-2">
                {userDetail.role !== 'admin' && (
                  <Button data-testid="suspend-user-btn" variant={userDetail.suspended ? 'default' : 'destructive'}
                    className="w-full rounded-full" onClick={() => toggleSuspend(userDetail.id)}>
                    {userDetail.suspended ? <><UserCheck className="w-4 h-4 mr-2" />Unsuspend User</> : <><Ban className="w-4 h-4 mr-2" />Suspend User</>}
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm text-slate-700 truncate">{value}</p>
      </div>
    </div>
  );
}

/* ═══════════════════ Agencies Panel + Drill-in ═══════════════════ */
function AgenciesPanel({ agencies, onRefresh }) {
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyDetail, setAgencyDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (a) => {
    setSelectedAgency(a);
    setDetailLoading(true);
    try {
      const res = await API.get(`/admin/agencies/${a.id}`);
      setAgencyDetail(res.data);
    } catch { toast.error('Failed to load agency details'); }
    finally { setDetailLoading(false); }
  };

  return (
    <div data-testid="admin-agencies-panel">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Agencies</h2>
        <p className="text-slate-500 text-sm mt-1">View registered agencies — click any row to drill in</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_120px_120px_100px] gap-4 px-5 py-3 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase tracking-wider">
          <span className="w-10" />
          <span>Agency</span>
          <span>Members</span>
          <span>Status</span>
          <span>Created</span>
        </div>
        <div className="divide-y divide-slate-100">
          {agencies.map(a => (
            <button key={a.id} data-testid={`agency-row-${a.id}`} onClick={() => openDetail(a)}
              className="w-full grid grid-cols-[auto_1fr_120px_120px_100px] gap-4 px-5 py-3.5 items-center hover:bg-slate-50 transition-colors text-left cursor-pointer">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-900 font-medium text-sm truncate">{a.name}</p>
                {a.website && <p className="text-slate-400 text-xs truncate">{a.website}</p>}
              </div>
              <span className="text-sm text-slate-600">{a.members_count || a.members?.length || '-'}</span>
              <StatusBadge status={a.verified_status || 'pending'} />
              <span className="text-xs text-slate-400">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</span>
            </button>
          ))}
        </div>
        {agencies.length === 0 && (
          <div className="text-center py-16">
            <Building2 className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No agencies registered yet.</p>
          </div>
        )}
      </div>

      {/* Agency Drill-in Sheet */}
      <Sheet open={!!selectedAgency} onOpenChange={o => { if (!o) { setSelectedAgency(null); setAgencyDetail(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="agency-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-lg font-heading">Agency Detail</SheetTitle>
            <SheetDescription className="sr-only">Detailed view of the selected agency</SheetDescription>
          </SheetHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-20"><div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full" /></div>
          ) : agencyDetail ? (
            <div className="mt-4 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-slate-900 font-semibold text-base">{agencyDetail.agency.name}</h3>
                  <StatusBadge status={agencyDetail.agency.verified_status || 'pending'} />
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-slate-900">{agencyDetail.total_deals}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Total Deals</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-slate-900">{agencyDetail.active_deals}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Active</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-slate-900">{agencyDetail.campaigns?.length || 0}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Campaigns</p>
                </div>
              </div>

              {/* Subscription */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Subscription</h4>
                {agencyDetail.subscription?.status ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Plan</span><span className="text-slate-900 font-medium capitalize">{agencyDetail.subscription.plan || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Status</span><StatusBadge status={agencyDetail.subscription.status} /></div>
                    {agencyDetail.subscription.current_period_end && (
                      <div className="flex justify-between"><span className="text-slate-500">Renews</span><span className="text-slate-700">{new Date(agencyDetail.subscription.current_period_end * 1000).toLocaleDateString()}</span></div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No active subscription</p>
                )}
              </div>

              {/* Agency info */}
              <div className="grid grid-cols-2 gap-3">
                {agencyDetail.agency.website && <InfoRow icon={ExternalLink} label="Website" value={agencyDetail.agency.website} />}
                {agencyDetail.agency.country && <InfoRow icon={MapPin} label="Country" value={agencyDetail.agency.country} />}
                {agencyDetail.agency.default_governing_law && <InfoRow icon={Shield} label="Governing Law" value={agencyDetail.agency.default_governing_law} />}
                <InfoRow icon={Calendar} label="Created" value={agencyDetail.agency.created_at ? new Date(agencyDetail.agency.created_at).toLocaleDateString() : '-'} />
              </div>

              {/* Team members */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Team Members ({agencyDetail.members?.length || 0})</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {(agencyDetail.members || []).map(m => (
                    <div key={m.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                      <Avatar className="w-8 h-8 border border-slate-200">
                        <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">{(m.user?.display_name || '?')[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{m.user?.display_name}</p>
                        <p className="text-xs text-slate-400 truncate">{m.user?.email}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px]">{m.role_in_agency || (m.roles || []).join(', ') || 'member'}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Campaigns */}
              {agencyDetail.campaigns && agencyDetail.campaigns.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Campaigns ({agencyDetail.campaigns.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {agencyDetail.campaigns.map(c => (
                      <div key={c.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3 text-xs" data-testid={`agency-campaign-${c.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 truncate">{c.name}</p>
                          <p className="text-slate-400">{c.deal_count || 0} deals</p>
                        </div>
                        <StatusBadge status={c.status || 'active'} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ═══════════════════ Disputes ═══════════════════ */
function DisputesPanel({ disputes, onRefresh }) {
  const [resolving, setResolving] = useState(null);
  const [form, setForm] = useState({ status: 'resolved', admin_notes: '', freeze_payout: false });

  const resolve = async (id) => {
    try {
      await API.post(`/admin/disputes/${id}/resolve`, form);
      toast.success('Dispute resolved'); setResolving(null); onRefresh();
    } catch { toast.error('Failed to resolve'); }
  };

  return (
    <div data-testid="admin-disputes-panel">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Disputes Queue</h2>
        <p className="text-slate-500 text-sm mt-1">Review and resolve user disputes</p>
      </div>
      <div className="space-y-4">
        {disputes.map(d => (
          <div key={d.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`admin-dispute-${d.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-slate-900 font-semibold">{d.reason}</h3>
                  <StatusBadge status={d.status} />
                </div>
                <p className="text-slate-500 text-sm">Opened by {d.opened_by?.display_name} &middot; License: <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{d.license_id}</code></p>
              </div>
              <p className="text-slate-500 text-xs">{new Date(d.created_at).toLocaleDateString()}</p>
            </div>
            <p className="text-slate-600 text-sm mb-4 p-4 bg-slate-50 rounded-xl">{d.description}</p>
            {d.status === 'open' && (
              resolving === d.id ? (
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger data-testid={`resolve-status-${d.id}`} className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200"><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="dismissed">Dismissed</SelectItem></SelectContent>
                  </Select>
                  <Textarea data-testid={`resolve-notes-${d.id}`} placeholder="Admin notes..." value={form.admin_notes} onChange={e => setForm({...form, admin_notes: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-900" rows={2} />
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-200">
                    <Switch data-testid={`freeze-payout-${d.id}`} checked={form.freeze_payout} onCheckedChange={v => setForm({...form, freeze_payout: v})} />
                    <Label className="text-red-700 text-sm">Freeze creator payout</Label>
                  </div>
                  <div className="flex gap-3">
                    <Button data-testid={`resolve-btn-${d.id}`} onClick={() => resolve(d.id)} className="bg-indigo-600 hover:bg-indigo-700 rounded-full">Submit Resolution</Button>
                    <Button variant="ghost" onClick={() => setResolving(null)} className="text-slate-500">Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button data-testid={`start-resolve-${d.id}`} variant="outline" onClick={() => setResolving(d.id)} className="rounded-full border-slate-200">Resolve Dispute</Button>
              )
            )}
            {d.admin_notes && <div className="mt-4 p-4 bg-indigo-50 rounded-xl border border-indigo-200"><p className="text-indigo-700 text-sm"><span className="font-medium">Resolution:</span> {d.admin_notes}</p></div>}
          </div>
        ))}
        {disputes.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No disputes in queue. All clear!</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════ Audit Logs (filterable + detail modal) ═══════════════════ */
function AuditLogsPanel({ logs: initialLogs, users, onRefresh }) {
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [logs, setLogs] = useState(initialLogs);

  useEffect(() => { setLogs(initialLogs); }, [initialLogs]);

  const fetchFiltered = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (userFilter !== 'all') params.set('user_id', userFilter);
      if (actionFilter !== 'all') params.set('action', actionFilter);
      const res = await API.get(`/admin/audit-logs?${params}`);
      setLogs(res.data.logs);
    } catch { toast.error('Failed to filter logs'); }
  }, [actionFilter, userFilter]);

  useEffect(() => { fetchFiltered(); }, [fetchFiltered]);

  const actions = [...new Set(initialLogs.map(l => l.action))].filter(Boolean).sort();
  const actorUsers = [...new Map(initialLogs.filter(l => l.actor?.display_name).map(l => [l.actor_id || l.actor?.id, l.actor])).values()];

  return (
    <div data-testid="admin-audit-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Audit Logs</h2>
          <p className="text-slate-500 text-sm mt-1">Track all platform activity — click any entry for details</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger data-testid="audit-user-filter" className="w-48 bg-white border-slate-200"><SelectValue placeholder="Filter by user" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200 max-h-60">
              <SelectItem value="all">All Users</SelectItem>
              {actorUsers.map(u => <SelectItem key={u.id || u.display_name} value={u.id || ''}>{u.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="audit-action-filter" className="w-56 bg-white border-slate-200"><SelectValue placeholder="Filter by action" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200 max-h-60">
              <SelectItem value="all">All Actions</SelectItem>
              {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-[140px_140px_120px_1fr_32px] gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase tracking-wider">
          <span>Timestamp</span>
          <span>Action</span>
          <span>Object</span>
          <span>Actor</span>
          <span />
        </div>
        <div className="divide-y divide-slate-100">
          {logs.map(log => (
            <button key={log.id} data-testid={`log-row-${log.id}`} onClick={() => setSelectedLog(log)}
              className="w-full grid grid-cols-[140px_140px_120px_1fr_32px] gap-3 px-5 py-3 items-center text-sm hover:bg-slate-50 transition-colors text-left cursor-pointer">
              <span className="text-slate-500 font-mono text-xs">{new Date(log.created_at).toLocaleString()}</span>
              <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-xs w-fit">{log.action}</Badge>
              <span className="text-slate-500 text-xs">{log.object_type}</span>
              <span className="text-slate-900 truncate font-medium text-xs">{log.actor?.display_name || 'System'}</span>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </button>
          ))}
        </div>
        {logs.length === 0 && <p className="text-center text-slate-500 py-12 text-sm">No audit logs match your filters.</p>}
      </div>

      {/* Audit log detail modal */}
      <Dialog open={!!selectedLog} onOpenChange={o => { if (!o) setSelectedLog(null); }}>
        <DialogContent className="max-w-md bg-white" data-testid="audit-log-detail-modal" aria-describedby="audit-log-desc">
          <DialogHeader><DialogTitle className="text-base font-heading">Audit Log Detail</DialogTitle></DialogHeader>
          <p id="audit-log-desc" className="sr-only">Detailed view of the selected audit log entry</p>
          {selectedLog && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-[10px] text-slate-400 uppercase tracking-wider">Action</p><Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-xs mt-0.5">{selectedLog.action}</Badge></div>
                <div><p className="text-[10px] text-slate-400 uppercase tracking-wider">Timestamp</p><p className="text-slate-700 mt-0.5">{new Date(selectedLog.created_at).toLocaleString()}</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase tracking-wider">Actor</p><p className="text-slate-700 mt-0.5">{selectedLog.actor?.display_name || 'System'}</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase tracking-wider">Object Type</p><p className="text-slate-700 mt-0.5">{selectedLog.object_type || '-'}</p></div>
                <div className="col-span-2"><p className="text-[10px] text-slate-400 uppercase tracking-wider">Object ID</p><p className="text-slate-700 font-mono text-xs mt-0.5 break-all">{selectedLog.object_id || '-'}</p></div>
              </div>
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Details</p>
                  <pre className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 overflow-auto max-h-48 whitespace-pre-wrap">{JSON.stringify(selectedLog.details, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════ Mediation ═══════════════════ */
const CASE_STATUS_META = {
  open: 'bg-amber-50 text-amber-600 border-amber-200',
  resolved_release: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  resolved_refund: 'bg-blue-50 text-blue-600 border-blue-200',
};

function MediationCaseRow({ c, statusMeta, onSelect }) {
  return (
    <button data-testid={`mediation-case-${c.id}`} onClick={() => onSelect(c)}
      className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {c.type === 'agency_refund_request'
            ? <RotateCcw className="w-4 h-4 text-blue-500" />
            : <Scale className="w-4 h-4 text-amber-500" />}
          <span className="text-sm font-semibold text-slate-800">
            {c.type === 'agency_refund_request' ? 'Refund requested by agency' : 'Release requested by creator'}
          </span>
        </div>
        <Badge variant="outline" className={`text-[10px] ${statusMeta[c.status] || ''}`}>{c.status.replace(/_/g, ' ')}</Badge>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        {c.context?.agency_name} ↔ {c.context?.creator_name} &middot; {c.currency} {c.amount?.toLocaleString()} &middot; {c.context?.campaign_name}
      </p>
      <p className="text-xs text-slate-600 mt-2 line-clamp-2">{c.reason}</p>
    </button>
  );
}

function MediationAdminPanel({ cases, onRefresh }) {
  const [selected, setSelected] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const open = cases.filter(c => c.status === 'open');
  const resolved = cases.filter(c => c.status !== 'open');

  const resolve = async () => {
    if (!outcome) { toast.error('Choose an outcome'); return; }
    setBusy(true);
    try {
      await API.post(`/admin/mediation/cases/${selected.id}/resolve`, { outcome, note });
      toast.success('Case resolved');
      setSelected(null); setOutcome(''); setNote('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const handleSelect = (c) => { setSelected(c); setOutcome(''); setNote(''); };

  return (
    <div data-testid="admin-mediation-panel">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Mediation</h2>
        <p className="text-slate-500 text-sm mt-1">Resolve refund &amp; release requests on held funds — outcomes move real money.</p>
      </div>

      {open.length > 0 && <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Open ({open.length})</h3>}
      <div className="space-y-3">{open.map(c => <MediationCaseRow key={c.id} c={c} statusMeta={CASE_STATUS_META} onSelect={handleSelect} />)}</div>

      {resolved.length > 0 && <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-2">Resolved</h3>}
      <div className="space-y-3">{resolved.map(c => <MediationCaseRow key={c.id} c={c} statusMeta={CASE_STATUS_META} onSelect={handleSelect} />)}</div>

      {cases.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
          <Scale className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">No mediation cases. All clear!</p>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg" aria-describedby="mediation-desc">
          <DialogHeader><DialogTitle className="text-base">Mediation case</DialogTitle></DialogHeader>
          <p id="mediation-desc" className="sr-only">Review the case and resolve with a money-moving decision.</p>
          {selected && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 p-3 text-xs space-y-1">
                <p><span className="text-slate-400">Parties:</span> {selected.context?.agency_name} ↔ {selected.context?.creator_name}</p>
                <p><span className="text-slate-400">Amount held:</span> {selected.currency} {selected.amount?.toLocaleString()} (net to creator: {selected.currency} {selected.context?.txn?.net_creator_payout?.toLocaleString()})</p>
                <p><span className="text-slate-400">Type:</span> {selected.type.replace(/_/g, ' ')}</p>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Conversation</Label>
                <div className="mt-1 space-y-2 max-h-48 overflow-auto pr-1">
                  {(selected.messages || []).map((m, i) => (
                    <div key={i} className={`text-xs rounded-lg p-2.5 ${m.by_role === 'admin' ? 'bg-violet-50 border border-violet-100' : m.by_role === 'agency' ? 'bg-blue-50' : 'bg-slate-50'}`}>
                      <span className="font-semibold capitalize text-slate-600">{m.by_role === 'admin' ? 'Mediator' : m.by_role}</span>
                      <p className="text-slate-700 mt-0.5 whitespace-pre-wrap">{m.message}</p>
                    </div>
                  ))}
                </div>
              </div>

              {selected.context?.submissions?.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Content submissions</Label>
                  <div className="mt-1 space-y-1">
                    {selected.context.submissions.map(s => (
                      <div key={s.id} className="text-xs text-slate-600 flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                        <span>{s.title || 'Untitled'}</span>
                        <Badge variant="outline" className="text-[9px]">{s.status?.replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.status === 'open' ? (
                <>
                  <div>
                    <Label className="text-xs text-slate-500">Decision</Label>
                    <Select value={outcome} onValueChange={setOutcome}>
                      <SelectTrigger data-testid="mediation-outcome-select" className="h-9 mt-1"><SelectValue placeholder="Choose an outcome..." /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="release_to_creator">Release funds to creator (Stripe Transfer)</SelectItem>
                        <SelectItem value="refund_to_agency">Refund the agency (Stripe Refund)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Decision note (shared with both parties)</Label>
                    <Textarea data-testid="mediation-note-input" value={note} onChange={e => setNote(e.target.value)} rows={2}
                      placeholder="Explain your decision..." className="mt-1 text-sm" />
                  </div>
                  <Button onClick={resolve} disabled={busy || !outcome} data-testid="mediation-resolve-btn"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                    {busy ? 'Processing...' : 'Resolve & move funds'}
                  </Button>
                </>
              ) : (
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-700">
                  Resolved: {selected.resolution?.outcome?.replace(/_/g, ' ')} {selected.resolution?.stripe_ref ? `· ${selected.resolution.stripe_ref}` : ''}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════ Support Tickets ═══════════════════ */
function SupportTicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ by_category: {}, by_status: {}, total: 0 });
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (status) params.status = status;
      const res = await API.get('/admin/support-tickets', { params });
      setTickets(res.data.tickets || []);
      setCounts(res.data.counts || { by_category: {}, by_status: {}, total: 0 });
    } catch (e) {
      toast.error('Failed to load support tickets');
    } finally { setLoading(false); }
  }, [category, status]);

  useEffect(() => { load(); }, [load]);

  const CATEGORIES = [
    { value: '', label: `All (${counts.total || 0})` },
    { value: 'trust_safety', label: `Trust & Safety (${counts.by_category.trust_safety || 0})`, tone: 'red' },
    { value: 'report_issue', label: `Report Issue (${counts.by_category.report_issue || 0})`, tone: 'amber' },
    { value: 'payout_billing', label: `Payout & Billing (${counts.by_category.payout_billing || 0})`, tone: 'emerald' },
    { value: 'campaign_support', label: `Campaign (${counts.by_category.campaign_support || 0})`, tone: 'blue' },
    { value: 'general', label: `General (${counts.by_category.general || 0})`, tone: 'slate' },
    { value: 'other', label: `Other (${counts.by_category.other || 0})`, tone: 'slate' },
  ];

  const updateStatus = async (ticketId, newStatus) => {
    try {
      await API.patch(`/admin/support-tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Marked ${newStatus.replace('_', ' ')}`);
      setSelected(null);
      load();
    } catch { toast.error('Failed to update ticket status'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-support-tickets">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Support Tickets</h1>
          <p className="text-slate-500 text-sm mt-0.5">{counts.total} total · {counts.by_status.open || 0} open · {counts.by_status.in_progress || 0} in progress · {counts.by_status.resolved || 0} resolved</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="support-refresh-btn">
          <RotateCcw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(c => (
          <button key={c.value || 'all'} onClick={() => setCategory(c.value)}
            data-testid={`support-filter-cat-${c.value || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${category === c.value ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">Status:</span>
        {[{v:'',l:'All'},{v:'open',l:'Open'},{v:'in_progress',l:'In Progress'},{v:'resolved',l:'Resolved'},{v:'closed',l:'Closed'}].map(s => (
          <button key={s.v || 'all'} onClick={() => setStatus(s.v)}
            data-testid={`support-filter-status-${s.v || 'all'}`}
            className={`px-2.5 py-1 rounded-md ${status === s.v ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-500 hover:text-slate-900'}`}>{s.l}</button>
        ))}
      </div>

      {/* Ticket list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {tickets.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">No tickets match this filter.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {tickets.map(t => (
              <button key={t.id} onClick={() => setSelected(t)}
                data-testid={`support-ticket-${t.id}`}
                className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${t.status === 'open' ? 'bg-red-500' : t.status === 'in_progress' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900 truncate">{t.subject}</p>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.category_label || t.category}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{t.user_email} · {t.user_role} · {new Date(t.created_at).toLocaleString()}</p>
                  <p className="text-xs text-slate-600 mt-1 line-clamp-1">{t.message}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 mt-1" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="support-ticket-detail">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{selected.category_label || selected.category}</span>
                <h3 className="text-lg font-bold text-slate-900 mt-1">{selected.subject}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{selected.user_name} · {selected.user_email} · {selected.user_role}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Ticket {selected.id} · {new Date(selected.created_at).toLocaleString()}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-900 flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Message</p>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
              {selected.context && Object.keys(selected.context).length > 0 && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Context</p>
                  {Object.entries(selected.context).map(([k, v]) => (
                    <p key={k} className="text-[11px] text-slate-600"><span className="text-slate-400">{k}:</span> {String(v)}</p>
                  ))}
                </div>
              )}
              <div className="pt-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Update status</p>
                <div className="flex gap-2 flex-wrap">
                  {['open','in_progress','resolved','closed'].map(s => (
                    <button key={s} onClick={() => updateStatus(selected.id, s)}
                      data-testid={`support-set-status-${s}`}
                      disabled={selected.status === s}
                      className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors ${selected.status === s ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}>
                      {s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

